import qs from 'query-string';

import client from 'core/axios';
import handleError from 'core/axios/handleError';
import {
  handleFileResponse,
  handleJSONResponse,
} from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';

const paramsSerializer = params =>
  qs.stringify(params, {
    arrayFormat: 'comma',
    encode: false,
  });

export const getProgramList = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCandidateProgramList = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/candidate-program-exploration/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getSponsorProgramList = ({
  version = 'v1',
  params = {},
  organization = '',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/sponsor-program-exploration/`,
      method: RequestMethod.GET,
      params: { organization, ...params },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createProgram = ({ version = 'v1', payload, params = {} } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateProgram = ({
  programId,
  version = 'v1',
  payload,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getProgram = ({ programId, version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getProgramCandidateDistricts = ({
  programId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidate-districts/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getProgramPopover = ({ version = 'v1', programId, params = {} }) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/program-popover/${programId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getProgramCount = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/count/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const getProgramPlacementStages = ({
  programId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/placement-stages/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPlacementCount = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/count/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCandidateList = ({
  programId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const getCandidateCount = ({
  programId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/count/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createCandidate = ({ programId, payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOpportunityList = ({
  programId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const getOpportunityCount = ({
  programId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/count/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createOpportunity = ({
  programId,
  payload,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const retrieveOpportunity = ({
  programId,
  oppId,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/${oppId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const retrieveOpportunitiesAsSponsor = ({
  orgUuid,
  programId,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/opportunity-exploration/`,
      method: RequestMethod.GET,
      params: { ...params, organization: orgUuid, program: programId },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateOpportunity = ({
  programId,
  oppId,
  payload,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/${oppId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const deleteOpportunity = ({
  programId,
  oppId,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/${oppId}/`,
      method: RequestMethod.DELETE,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const retrieveCandidate = ({
  programId,
  candidateId,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/${candidateId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const retrieveCandidateAsStudent = ({
  programId,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/current-user/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateCandidate = ({
  programId,
  candidateId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/${candidateId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const deleteCandidate = ({
  programId,
  candidateId,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/${candidateId}/`,
      method: RequestMethod.DELETE,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPlacement = ({ placementId, version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placement-details/${placementId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createPlacement = ({
  payload,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deletePlacement = ({
  placementId,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/${placementId}/`,
      method: RequestMethod.DELETE,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCandidatePlacements = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/candidate-placements/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getSponsorPlacements = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/sponsor-placements/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used by program managers only
 * @param params.organization {string} - org uuid
 */
export const unpublishPlacement = ({
  placementId,
  payload,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/${placementId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const getPlacementList = ({
  version = 'v1',
  params = {},
  programId = undefined,
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/`,
      method: RequestMethod.GET,
      params: { ...params, ...{ program: programId || params.program } },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getProgramPlacementList = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/program-placements/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param programId {string} - the current program id
 * @param params.organization {string} - org uuid
 */
export const createShortlist = ({
  programId,
  payload,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/shortlists/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param programId {string} - the current program id
 * @param params.organization {string} - org uuid
 */
export const deleteShortlist = ({
  programId,
  shortlistId,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/shortlists/${shortlistId}/`,
      method: RequestMethod.DELETE,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param programId {string} - the current program id
 * @param params.organization {string} - org uuid
 */
export const getShortlistList = ({
  version = 'v1',
  programId,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/shortlists/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getLocationTypeList = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/location-types/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getProgramTypeList = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/program-types/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOrganizationsList = ({
  programId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/organizations/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const getPlacedStudents = ({
  programId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/placed-students/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const bulkMovePlacementsStage = ({
  payload,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/bulk-move-stage/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const bulkPublishPlacements = ({
  target,
  payload,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/bulk-publish-for-${target}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getFields = ({ programId, version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/fields/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const createField = ({
  programId,
  version = 'v1',
  payload,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/fields/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const updateField = ({
  programId,
  fieldId,
  version = 'v1',
  params = {},
  payload,
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/fields/${fieldId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const deleteField = ({
  programId,
  fieldId,
  version = 'v1',
  payload,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/fields/${fieldId}/`,
      method: RequestMethod.DELETE,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getFieldOptions = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/field-options/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const createFieldOption = ({
  version = 'v1',
  payload,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/field-options/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const updateFieldOption = ({
  fieldOptionId,
  version = 'v1',
  payload,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/field-options/${fieldOptionId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const deleteFieldOption = ({
  fieldOptionId,
  version = 'v1',
  payload,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/field-options/${fieldOptionId}/`,
      method: RequestMethod.DELETE,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCandidateFreeResponses = ({
  programId,
  candidateId,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/${candidateId}/free-responses/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOpportunityFreeResponses = ({
  programId,
  opportunityId,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/${opportunityId}/free-responses/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createCandidateFreeResponse = ({
  programId,
  candidateId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/${candidateId}/free-responses/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createOpportunityFreeResponse = ({
  programId,
  opportunityId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/${opportunityId}/free-responses/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateCandidateFreeResponse = ({
  programId,
  candidateId,
  freeResponseId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/${candidateId}/free-responses/${freeResponseId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateOpportunityFreeResponse = ({
  programId,
  opportunityId,
  freeResponseId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/${opportunityId}/free-responses/${freeResponseId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteCandidateFreeResponse = ({
  programId,
  candidateId,
  freeResponseId,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/${candidateId}/free-responses/${freeResponseId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteOpportunityFreeResponse = ({
  programId,
  opportunityId,
  freeResponseId,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/${opportunityId}/free-responses/${freeResponseId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCandidateFileResponses = ({
  programId,
  candidateId,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/${candidateId}/files/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOpportunityFileResponses = ({
  programId,
  opportunityId,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/${opportunityId}/files/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createCandidateFileResponse = ({
  programId,
  candidateId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/${candidateId}/files/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createOpportunityFileResponse = ({
  programId,
  opportunityId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/${opportunityId}/files/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteCandidateFileResponse = ({
  programId,
  candidateId,
  fileResponseId,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/${candidateId}/files/${fileResponseId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteOpportunityFileResponse = ({
  programId,
  opportunityId,
  fileResponseId,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/${opportunityId}/files/${fileResponseId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCandidateDateResponses = ({
  programId,
  candidateId,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/${candidateId}/date-responses/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOpportunityDateResponses = ({
  programId,
  opportunityId,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/${opportunityId}/date-responses/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createCandidateDateResponse = ({
  programId,
  candidateId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/${candidateId}/date-responses/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createOpportunityDateResponse = ({
  programId,
  opportunityId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/${opportunityId}/date-responses/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateCandidateDateResponse = ({
  programId,
  candidateId,
  dateResponseId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/${candidateId}/date-responses/${dateResponseId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateOpportunityDateResponse = ({
  programId,
  opportunityId,
  dateResponseId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/${opportunityId}/date-responses/${dateResponseId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteCandidateDateResponse = ({
  programId,
  candidateId,
  dateResponseId,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/${candidateId}/date-responses/${dateResponseId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteOpportunityDateResponse = ({
  programId,
  opportunityId,
  dateResponseId,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/${opportunityId}/date-responses/${dateResponseId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPlacementStages = ({
  programId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/placement-stages/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const reorderPlacementStages = ({
  programId,
  version = 'v1',
  payload,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/placement-stages/reorder/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createPlacementStage = ({
  programId,
  version = 'v1',
  payload,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/placement-stages/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updatePlacementStage = ({
  programId,
  placementStageId,
  version = 'v1',
  payload,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/placement-stages/${placementStageId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deletePlacementStage = ({
  programId,
  placementStageId,
  version = 'v1',
  payload,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/placement-stages/${placementStageId}/`,
      method: RequestMethod.DELETE,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getForms = ({ programId, version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/documents/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createForm = ({
  programId,
  version = 'v1',
  payload,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/documents/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateForm = ({
  programId,
  formId,
  version = 'v1',
  payload,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/documents/${formId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteForm = ({
  programId,
  formId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/documents/${formId}/`,
      method: RequestMethod.DELETE,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getFormResponsibleParties = ({
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/document-responsible-parties/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createFormResponsibleParty = ({
  version = 'v1',
  payload,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/document-responsible-parties/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteFormResponsibleParty = ({
  formResponsiblePartyId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/document-responsible-parties/${formResponsiblePartyId}/`,
      method: RequestMethod.DELETE,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createPlacementForm = ({ version = 'v1', payload } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placement-documents/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createCustomPlacementForm = ({ version = 'v1', payload } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placement-documents/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPlacementForms = ({ version = 'v1', placementId } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placement-documents/`,
      method: RequestMethod.GET,
      params: { placement: placementId },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deletePlacementForm = ({ version = 'v1', placementFormId } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placement-documents/${placementFormId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentScope = ({ version = 'v1', scopeId }) =>
  client
    .request({
      url: `${version}/k12-users/student-scope/${scopeId}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createStudentScope = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/k12-users/student-scope/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateStudentScope = ({ version = 'v1', scopeId, payload }) =>
  client
    .request({
      url: `${version}/k12-users/student-scope/${scopeId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const getIndependentProgramSurveys = ({
  version = 'v1',
  programId,
  placementId: placement,
  params = {},
}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/surveys/independent/`,
      method: RequestMethod.GET,
      params: {
        placement,
        ...params,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getProgramSurveysForPlacement = ({
  version = 'v1',
  programId,
  placementId,
}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/surveys/`,
      method: RequestMethod.GET,
      params: {
        placement: placementId,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const getProgramSurveySequences = ({
  version = 'v1',
  programId,
  placementId: placement,
  params = {},
}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/survey-sequences/`,
      method: RequestMethod.GET,
      params: {
        placement,
        ...params,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getProgramSurveySequence = ({
  version = 'v1',
  programId,
  placementId,
  sequenceId,
  params = {},
}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/survey-sequences/${sequenceId}/`,
      method: RequestMethod.GET,
      params: {
        placement: placementId,
        ...params,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getResponsibleParties = ({ version = 'v1', params = {} }) =>
  client
    .request({
      url: `${version}/ip-program-management/responsible-parties/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createProgramIndependentSurvey = ({
  version = 'v1',
  programId,
  payload,
  params = {},
}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/surveys/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createProgramSurveySequence = ({
  version = 'v1',
  programId,
  payload,
  params = {},
}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/survey-sequences/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateProgramSurveySequence = ({
  version = 'v1',
  programId,
  payload,
  sequenceId,
  params = {},
}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/survey-sequences/${sequenceId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateProgramIndependentSurvey = ({
  version = 'v1',
  programId,
  surveyId,
  payload,
  params = {},
}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/surveys/${surveyId}`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteProgramIndependentSurvey = ({
  programId,
  surveyId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/surveys/${surveyId}/`,
      method: RequestMethod.DELETE,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteProgramSurveySequence = ({
  programId,
  sequenceId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/survey-sequences/${sequenceId}/`,
      method: RequestMethod.DELETE,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const addUserSurveyToPlacement = ({
  version = 'v1',
  placementId,
  userSurveyId,
  params = {},
  payload = {},
}) =>
  client
    .request({
      url: `${version}/ip-program-management/placement-details/${placementId}/add-user-survey/${userSurveyId}/`,
      method: RequestMethod.POST,
      params,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const getCandidateCsvReport = ({
  payload,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/candidates-csv-report/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const getFullApplicationDetailsCsvReport = ({
  payload,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/candidates-opportunities-csv-report/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param programId {string} - program id
 * @param params {object} - any settings for the report or candidate filters
 * @param params.ids {string[]} - candidate ids. if left empty, all candidates with matching filters on the program will be included
 * @param params.redacted {boolean} - whether to redact personal information (email/name)
 * @param params.combinedPDFs {boolean} - whether to combine all reports into a single PDF file
 */
export const getCandidateApplicationsPDFReport = ({
  programId,
  params = {},
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates-data-export/`,
      method: RequestMethod.POST,
      responseType: 'blob',
      params,
    })
    .then(handleFileResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const getOpportunityCsvReport = ({
  payload,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/opportunities-csv-report/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param payload.programId {string}
 * @param payload.ids {string[]}
 * @param params.organization {string} - org uuid
 */
export const getPlacementCsvReport = ({
  payload,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements-csv-report/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param payload.programId {string}
 * @param payload.ids {string[]}
 * @param params.organization {string} - org uuid
 */
export const getTimesheetCsvReport = ({
  payload,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements-timesheet-csv-report/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPlacementFormStatusCsvReport = ({
  payload,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/form-details-report/`,
      method: RequestMethod.POST,
      data: { ...payload },
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used to reorder application questions
 * @param programId
 * @param questionId
 * @param payload - { order: NEW_INDEX_VALUE }, NEW_INDEX_VALUE must be greater
 * than 0
 */
export const reorderApplicationQuestions = ({
  version = 'v1',
  programId,
  questionId,
  payload,
  params = {},
}) => {
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/fields/${questionId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);
};

/**
 * @param params.organization {string} - org uuid
 */
export const getProgramSurveyAnalytics = ({
  programHashId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/program-survey-analytics/${programHashId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const getProgramSurveyAnalyticsPlacementView = ({
  programId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/placement-heatmap/${programId}`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.organization {string} - org uuid
 */
export const getProgramSurveyAnalyticsCount = ({
  programId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/surveys/count/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPlacementHashContext = ({
  placementHash,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/placement-hash-context/${placementHash}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param programId {string} - the current program id
 */
export const sendCustomEmail = ({
  programId,
  payload,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/send-custom-email/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createPlacementHash = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/create-hash/`,
      method: RequestMethod.GET,
      params,
      paramsSerializer: { serialize: paramsSerializer },
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used by program managers to retrieve candidate class years
 * @param params.organization {string} - org uuid
 */
export const getCandidatesClassYears = ({
  programId,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidates/class-years/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEvaluationDetails = ({
  programId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/evaluation-details/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEvaluationResponses = ({
  placementId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placement-details/${placementId}/placement-evaluation-sections/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used to submit responses for evaluations in placements
 * @param payload.section {number} - section (column) for which responses are being submitted
 * @param payload.responses {Object[]} - array of responses
 */
export const createEvaluationResponses = ({
  placementId,
  version = 'v1',
  payload,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placement-details/${placementId}/placement-evaluation-sections/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteEvaluationResponses = ({
  placementId,
  responseId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placement-details/${placementId}/placement-evaluation-prompt-responses/${responseId}/`,
      method: RequestMethod.DELETE,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used to fetch the sync history table list
 * @param programId {number}
 * @param params.ordering {string} - Any of these created, removed_candidates_count, added_candidates_count
 */
export const getSyncHistoryList = ({
  programId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/caseload-sync-logs/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get the removed candidates for a given caseload sync history log
 * @param programId {number}
 * @param caseloadSyncLogId {number}
 * @param params.ordering {string} - Any of these created, removed_candidates_count, added_candidates_count
 */
export const getRemovedCaseloadSyncLog = ({
  programId,
  caseloadSyncLogId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/caseload-sync-logs/${caseloadSyncLogId}/candidates/removed/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get the added candidates for a given caseload sync history log
 * @param programId {number}
 * @param caseloadSyncLogId {number}
 * @param params.ordering {string} - Any of these created, removed_candidates_count, added_candidates_count
 */
export const getAddedCaseloadSyncLog = ({
  programId,
  caseloadSyncLogId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/caseload-sync-logs/${caseloadSyncLogId}/candidates/added/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Clone a program
 * @param programId {number}
 * @param payload.name {string}
 * @param payload.programManagers {string[]}
 * @param payload.placementManager {string[]}
 * @param payload.copyCandidates {bool}
 * @param payload.enableCaseload {bool}
 * @param payload.syncCaseloads {string[]}
 * @param payload.copyEvaluation {bool}
 * @param payload.copyOpportunities {bool}
 */
export const cloneProgram = ({
  programId,
  version = 'v1',
  payload = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/clone/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param payload.program {number} - The program id
 */
export const createBYOPPlacement = ({ version = 'v1', payload } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/student-sourced/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateBYOPSponsor = ({
  placementId,
  version = 'v1',
  payload,
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/${placementId}/sponsor/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const submitBYOPCandidate = ({ version = 'v1', placementId } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/${placementId}/submit/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param hashId - the placement hash id
 */
export const getPublicBYOPInfo = ({
  hashId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/${hashId}/public/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const acceptBYOPAsSponsor = ({ version = 'v1', placementId } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/${placementId}/accept/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const approveBYOPAsProgramManager = ({
  version = 'v1',
  placementId,
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/${placementId}/approve/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const resetBYOPApplicationStatus = ({
  version = 'v1',
  placementId,
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placements/${placementId}/reset/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getHeatmapAggregate = ({
  version = 'v1',
  programId,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/placement-heatmap/${programId}/aggregate/`,
      method: RequestMethod.GET,
      params,
      paramsSerializer: { serialize: paramsSerializer },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const sendHeatmapEmail = ({ version = 'v1', programId, payload } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/placement-heatmap/${programId}/email/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateCandidatePreferences = ({
  version = 'v1',
  payload = {},
  userId,
  programId,
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/candidate-preferences/${userId}/`,
      method: RequestMethod.PUT,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.combinedPdf {bool} - whether to combine the pdfs or not
 * @param params.blankPages {bool} - whether to include blank pages or not
 * @param params.placementIds {string} - comma separated list of placement ids
 */
export const requestFormSequencesDataExport = ({
  version = 'v1',
  programId,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/form-sequences-data-export/`,
      method: RequestMethod.POST,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param params.candidate {number} - Get preferences for a specific candidate by id
 * @param params.opportunity {number} - Get preferences for a specific opportunity by id
 */
export const getPreferences = ({
  programId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/preferences/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get all auto filter rules for a program (for a given user - determined by the token)
 * @param {string} programId - Program ID
 */
export const getProgramAutoFilterRules = ({ version = 'v1', programId } = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/auto-filter-rules/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Create auto filter rule for a program
 * @param {string} programId - Program ID
 * @param {Object} payload - `{ field: number, allowPartialMatch: bool}`
 */
export const createProgramAutoFilterRule = ({
  version = 'v1',
  programId,
  payload,
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/auto-filter-rules/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Update auto filter rule for a program
 * @param {string} programId - Program ID
 * @param {string} autoFilterId - Auto filter ID
 * @param {Object} payload - `{ allowPartialMatch: bool}`
 */
export const updateProgramAutoFilterRule = ({
  version = 'v1',
  programId,
  autoFilterRuleId,
  payload,
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/auto-filter-rules/${autoFilterRuleId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Delete auto filter rule for a program
 * @param {string} programId - Program ID
 * @param {string} autoFilterId - Auto filter ID
 */
export const deleteProgramAutoFilterRule = ({
  version = 'v1',
  programId,
  autoFilterRuleId,
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/auto-filter-rules/${autoFilterRuleId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOpportunityCreatedByOptions = ({
  programId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/opportunities/created-by-options/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Update program section status
 * @param {string} version - API version
 * @param {string} programId - Program ID
 * @param {string} sectionId - Section ID
 * @param {string} status - Section status (complete or incomplete)
 */
export const updateProgramSectionStatus = ({
  version = 'v1',
  programId,
  sectionId,
  status,
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/section/${sectionId}/${status}/`,
      method: RequestMethod.PUT,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get program summary (counts for candidates, sponsors, and placements)
 * @param {string} version - API version
 * @param {string} programId - Program ID
 * @param {Object} params - Query parameters
 */
export const getProgramSummary = ({
  version = 'v1',
  programId,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/programs/${programId}/summary/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get placement evaluation completion summary
 * @param {string} version - API version
 * @param {string} placementId - Placement ID
 * @param {Object} params - Query parameters
 */
export const getPlacementEvaluationCompletion = ({
  version = 'v1',
  placementId,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/ip-program-management/placement-details/${placementId}/placement-evaluation-sections/summary/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);
